import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import axios from 'axios';
import { 
    Calendar, 
    RefreshCw, 
    IndianRupee, 
    FileText, 
    TrendingUp, 
    CheckCircle2, 
    Clock, 
    AlertCircle,
    Receipt,
    Percent,
    Wallet,
    Search,
    ChevronDown,
    ArrowUpRight,
    Sparkles,
    Layers,
    BadgeCheck
} from 'lucide-react';

// Total number of financial years to generate in the dropdown
const TOTAL_YEARS_COUNT = 20;
const BASE_URL = "https://backend.creditjar.in";

/**
 * Calculates current Indian Financial Year (April 1 to March 31)
 * Example: October 2026 -> "2026-2027", February 2026 -> "2025-2026"
 */
const getCurrentFinancialYear = () => {
    const today = new Date();
    const currentMonth = today.getMonth(); // 0 = Jan, 3 = Apr, 11 = Dec
    const currentYear = today.getFullYear();
    const startYear = currentMonth >= 3 ? currentYear : currentYear - 1;
    return `${startYear}-${startYear + 1}`;
};

/**
 * Generates an array of financial years going back `yearsCount` years from current FY
 */
const generateFinancialYearsList = (yearsCount = TOTAL_YEARS_COUNT) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const currentFYStart = currentMonth >= 3 ? currentYear : currentYear - 1;

    const yearsList = [];
    for (let i = 0; i < yearsCount; i++) {
        const start = currentFYStart - i;
        const end = start + 1;
        yearsList.push(`${start}-${end}`);
    }
    return yearsList;
};

export default function Payouts({ communityId }) {
    const financialYears = useMemo(() => generateFinancialYearsList(TOTAL_YEARS_COUNT), []);
    const [selectedFY, setSelectedFY] = useState(getCurrentFinancialYear());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [statementData, setStatementData] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("ALL");

    const fetchStatement = async (fyToFetch) => {
        if (!communityId) {
            setError("Community ID is missing");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const url = `${BASE_URL}/community/commission/statement?communityId=${communityId}&financialYear=${fyToFetch || selectedFY}`;
            const response = await axios.get(url, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (response?.data?.status === 200 || response?.data?.message === "success") {
                setStatementData(response?.data?.data || null);
            } else if (response?.data?.data) {
                setStatementData(response.data.data);
            } else {
                setStatementData(null);
            }
        } catch (err) {
            console.error("Error fetching commission statement:", err);
            setError(err?.response?.data?.message || err?.message || "Failed to fetch commission statement");
            setStatementData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (communityId && selectedFY) {
            fetchStatement(selectedFY);
        }
    }, [communityId, selectedFY]);

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined || isNaN(Number(amount))) return "₹0.00";
        return "₹" + Number(amount).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return "-";
        const num = Number(dateValue);
        if (!isNaN(num) && num > 0) {
            return moment(num).format("DD-MM-YYYY");
        }
        const m = moment(dateValue);
        return m.isValid() ? m.format("DD-MM-YYYY") : "-";
    };

    const getStatusBadge = (status) => {
        const s = (status || "").toUpperCase();
        if (s === "PAID" || s === "SUCCESS" || s === "COMPLETED") {
            return {
                bg: "rgba(16, 185, 129, 0.1)",
                color: "#059669",
                border: "rgba(16, 185, 129, 0.25)",
                dotColor: "#10b981",
                label: s
            };
        }
        if (s === "PENDING" || s === "IN_PROGRESS" || s === "PROCESSING") {
            return {
                bg: "rgba(245, 158, 11, 0.1)",
                color: "#d97706",
                border: "rgba(245, 158, 11, 0.25)",
                dotColor: "#f59e0b",
                label: s
            };
        }
        if (s === "FAILED" || s === "REJECTED") {
            return {
                bg: "rgba(239, 68, 68, 0.1)",
                color: "#dc2626",
                border: "rgba(239, 68, 68, 0.25)",
                dotColor: "#ef4444",
                label: s
            };
        }
        return {
            bg: "#f1f5f9",
            color: "#64748b",
            border: "#cbd5e1",
            dotColor: "#94a3b8",
            label: s || "UNKNOWN"
        };
    };

    // Extract loans commissions list safely
    const loanCommissionsList = useMemo(() => {
        if (!statementData) return [];
        if (Array.isArray(statementData.loanCommissions)) {
            return statementData.loanCommissions;
        }
        if (statementData.loanCommissions && Array.isArray(statementData.loanCommissions.loanCommissions)) {
            return statementData.loanCommissions.loanCommissions;
        }
        return [];
    }, [statementData]);

    const filteredCommissions = useMemo(() => {
        return loanCommissionsList.filter(item => {
            const matchesStatus = filterStatus === "ALL" || (item.payoutStatus || "").toUpperCase() === filterStatus;
            if (!matchesStatus) return false;
            if (!searchTerm.trim()) return true;
            const q = searchTerm.toLowerCase();
            const amount = String(item.loanAmount || "");
            const commAmt = String(item.commissionAmount || "");
            const netAmt = String(item.netPayoutAmount || "");
            const dateStr = formatDate(item.disburseDate);
            return amount.includes(q) || commAmt.includes(q) || netAmt.includes(q) || dateStr.includes(q);
        });
    }, [loanCommissionsList, searchTerm, filterStatus]);

    return (
        <div style={{
            padding: '24px',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            color: '#0f172a',
            backgroundColor: '#f8fafc',
            minHeight: '100vh'
        }}>
            
            {/* Top Hero / Header Toolbar */}
            <div style={{
                background: '#ffffff',
                borderRadius: '16px',
                padding: '22px 28px',
                boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.03)',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '20px',
                marginBottom: '28px'
            }}>
                {/* Title & Info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                        color: '#ffffff',
                        width: '50px',
                        height: '50px',
                        borderRadius: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '12px',
                        boxShadow: '0 8px 16px -4px rgba(2, 132, 199, 0.35)'
                    }}>
                        <Wallet style={{ width: "100%", margin: "auto" }} />
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.02em' }}>
                                Commission & Payouts
                            </h2>
                            <span style={{
                                background: '#eff6ff',
                                color: '#1d4ed8',
                                fontSize: '12px',
                                fontWeight: '700',
                                padding: '3px 10px',
                                borderRadius: '20px',
                                border: '1px solid #bfdbfe',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                <Sparkles style={{ width: '12px', height: '12px', margin: 'auto' }} />
                                FY {selectedFY}
                            </span>
                        </div>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                            Consolidated disbursement statement, TDS compliance, and net commission tracking
                        </p>
                    </div>
                </div>

                {/* Filters & Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {/* Financial Year Selector */}
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: '#f1f5f9',
                        borderRadius: '10px',
                        padding: '3px 5px',
                        border: '1px solid #e2e8f0',
                        height: '40px',
                        boxSizing: 'border-box'
                    }}>
                        <span style={{
                            padding: '0 8px',
                            fontSize: '12px',
                            fontWeight: '700',
                            color: '#475569',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            FY
                        </span>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <select
                                value={selectedFY}
                                onChange={(e) => setSelectedFY(e.target.value)}
                                style={{
                                    appearance: 'none',
                                    padding: '0 32px 0 12px',
                                    height: '32px',
                                    fontSize: '13.5px',
                                    fontWeight: '700',
                                    color: '#0f172a',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                    minWidth: '150px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                {financialYears.map((fy, index) => (
                                    <option key={fy} value={fy}>
                                        {fy} {index === 0 ? " (Current)" : ""}
                                    </option>
                                ))}
                            </select>
                            <div style={{ position: 'absolute', right: '10px',top:'4px', pointerEvents: 'none', color: '#64748b', width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ChevronDown style={{ width: "100%", margin: "auto", marginBottom:'-10px' }} />
                            </div>
                        </div>
                    </div>

                    {/* Refresh Button */}
                    <button
                        onClick={() => fetchStatement(selectedFY)}
                        disabled={loading}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '40px',
                            boxSizing: 'border-box',
                            gap: '7px',
                            background: '#ffffff',
                            color: '#334155',
                            border: '1px solid #cbd5e1',
                            padding: '0 16px',
                            borderRadius: '10px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                    >
                        <div style={{ width: '14px', height: '14px', display: 'inline-flex', alignItems: 'center',marginBottom:'10px' }}>
                            <RefreshCw className={loading ? "fa-spin" : ""} style={{ width: "100%", margin: "auto" }} />
                        </div>
                        <span>Refresh</span>
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#991b1b',
                    padding: '16px 20px',
                    borderRadius: '12px',
                    marginBottom: '28px',
                    boxShadow: '0 2px 6px rgba(239, 68, 68, 0.08)'
                }}>
                    <div style={{ width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                        <AlertCircle style={{ width: "100%", margin: "auto" }} />
                    </div>
                    <div>
                        <strong style={{ display: 'block', fontSize: '13.5px' }}>Error loading statement</strong>
                        <span style={{ fontSize: '13px', color: '#b91c1c' }}>{error}</span>
                    </div>
                </div>
            )}

            {/* Loading Skeleton */}
            {loading && (
                <div style={{ marginTop: '28px', marginBottom: '36px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '18px', marginBottom: '28px' }}>
                        {[1, 2, 3, 4, 5].map((item) => (
                            <div key={item} style={{
                                background: '#ffffff',
                                borderRadius: '16px',
                                padding: '20px',
                                border: '1px solid #e2e8f0',
                                height: '130px',
                                animation: 'pulse 1.5s infinite ease-in-out'
                            }} />
                        ))}
                    </div>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '16px',
                        padding: '40px',
                        border: '1px solid #e2e8f0',
                        height: '260px',
                        animation: 'pulse 1.5s infinite ease-in-out'
                    }} />
                    <style>{`
                        @keyframes pulse {
                            0% { opacity: 1; }
                            50% { opacity: 0.5; }
                            100% { opacity: 1; }
                        }
                    `}</style>
                </div>
            )}

            {!loading && statementData && (
                <>
                    {/* Modern Stats / KPI Cards Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: '18px',
                        marginTop: '28px',
                        marginBottom: '36px'
                    }}>
                        {/* Card 1: Total Disbursed Loans */}
                        <div style={{
                            background: '#ffffff',
                            padding: '22px',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 4px 15px -3px rgba(0, 0, 0, 0.04), 0 2px 6px -2px rgba(0, 0, 0, 0.02)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: '#0284c7' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Total Loans
                                </span>
                                <div style={{ background: '#f0f9ff', color: '#0284c7', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '9px', borderRadius: '10px' }}>
                                    <FileText style={{ width: "100%", margin: "auto" }} />
                                </div>
                            </div>
                            <div style={{ marginTop: '16px' }}>
                                <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.02em', lineHeight: '1' }}>
                                    {statementData.totalLoans ?? 0}
                                </div>
                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ fontWeight: '600', color: '#0284c7' }}>100%</span> processed in FY {selectedFY}
                                </div>
                            </div>
                        </div>

                        {/* Card 2: Total Disbursed Volume */}
                        <div style={{
                            background: '#ffffff',
                            padding: '22px',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 4px 15px -3px rgba(0, 0, 0, 0.04), 0 2px 6px -2px rgba(0, 0, 0, 0.02)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: '#059669' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Disbursed Volume
                                </span>
                                <div style={{ background: '#ecfdf5', color: '#059669', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '9px', borderRadius: '10px' }}>
                                    <TrendingUp style={{ width: "100%", margin: "auto" }} />
                                </div>
                            </div>
                            <div style={{ marginTop: '16px' }}>
                                <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.02em', lineHeight: '1.1' }}>
                                    {formatCurrency(statementData.totalDisbursedAmount)}
                                </div>
                                <div style={{ fontSize: '12px', color: '#059669', marginTop: '8px', fontWeight: '600' }}>
                                    {statementData.configuredCommissionPercent ?? 0}% Commission Rate
                                </div>
                            </div>
                        </div>

                        {/* Card 3: Gross Commission */}
                        <div style={{
                            background: '#ffffff',
                            padding: '22px',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 4px 15px -3px rgba(0, 0, 0, 0.04), 0 2px 6px -2px rgba(0, 0, 0, 0.02)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: '#7c3aed' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Gross Commission
                                </span>
                                <div style={{ background: '#f5f3ff', color: '#7c3aed', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '9px', borderRadius: '10px' }}>
                                    <Receipt style={{ width: "100%", margin: "auto" }} />
                                </div>
                            </div>
                            <div style={{ marginTop: '16px' }}>
                                <div style={{ fontSize: '24px', fontWeight: '800', color: '#7c3aed', letterSpacing: '-0.02em', lineHeight: '1.1' }}>
                                    {formatCurrency(statementData.totalCommissionEarned)}
                                </div>
                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
                                    Gross earnings before TDS
                                </div>
                            </div>
                        </div>

                        {/* Card 4: Total TDS Deducted */}
                        <div style={{
                            background: '#ffffff',
                            padding: '22px',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 4px 15px -3px rgba(0, 0, 0, 0.04), 0 2px 6px -2px rgba(0, 0, 0, 0.02)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: '#ea580c' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    TDS Deducted ({statementData.configuredTdsPercent ?? 0}%)
                                </span>
                                <div style={{ background: '#fff7ed', color: '#ea580c', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '9px', borderRadius: '10px' }}>
                                    <Percent style={{ width: "100%", margin: "auto" }} />
                                </div>
                            </div>
                            <div style={{ marginTop: '16px' }}>
                                <div style={{ fontSize: '24px', fontWeight: '800', color: '#ea580c', letterSpacing: '-0.02em', lineHeight: '1.1' }}>
                                    {formatCurrency(statementData.totalTdsDeducted)}
                                </div>
                                <div style={{ marginTop: '8px' }}>
                                    {statementData.tdsThresholdCrossed ? (
                                        <span style={{
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            padding: '2px 8px',
                                            borderRadius: '6px',
                                            backgroundColor: '#fef2f2',
                                            color: '#b91c1c',
                                            border: '1px solid #fecaca',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '3px',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            <AlertCircle style={{ width: '11px', height: '11px', margin: 'auto' }} />
                                            Threshold Crossed (&gt;{formatCurrency(statementData.tdsThreshold)})
                                        </span>
                                    ) : (
                                        <span style={{
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            padding: '2px 8px',
                                            borderRadius: '6px',
                                            backgroundColor: '#f8fafc',
                                            color: '#475569',
                                            border: '1px solid #e2e8f0',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            Below {formatCurrency(statementData.tdsThreshold)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Card 5: Net Payable Payout (Hero Highlight Card) */}
                        <div style={{
                            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #064e3b 100%)',
                            color: '#ffffff',
                            padding: '22px',
                            borderRadius: '16px',
                            border: '1px solid rgba(16, 185, 129, 0.4)',
                            boxShadow: '0 10px 25px -5px rgba(6, 78, 59, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {/* Decorative background glow */}
                            <div style={{
                                position: 'absolute',
                                right: '-20px',
                                top: '-20px',
                                width: '110px',
                                height: '110px',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(16, 185, 129, 0.25) 0%, transparent 70%)',
                                pointerEvents: 'none'
                            }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#a7f3d0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Net Payout (Payable)
                                </span>
                                <div style={{ background: 'rgba(255, 255, 255, 0.12)', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '9px', borderRadius: '10px' }}>
                                    <IndianRupee style={{ width: "100%", margin: "auto", color: "#34d399" }} />
                                </div>
                            </div>
                            <div style={{ marginTop: '16px' }}>
                                <div style={{ fontSize: '26px', fontWeight: '900', color: '#ffffff', letterSpacing: '-0.02em', lineHeight: '1.1' }}>
                                    {formatCurrency(statementData.totalNetPayout)}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6ee7b7', marginTop: '8px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <BadgeCheck style={{ width: '14px', height: '14px', margin: '0' }} />
                                    Final Disbursal Amount
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Breakdown Table Section */}
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.05)',
                        overflow: 'hidden'
                    }}>
                        {/* Table Header Controls */}
                        <div style={{
                            padding: '20px 24px',
                            borderBottom: '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '16px',
                            backgroundColor: '#fafbfc'
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                                    Disbursed Loan Transactions
                                </h3>
                                <p style={{ margin: '3px 0 0 0', fontSize: '12.5px', color: '#64748b' }}>
                                    Showing {filteredCommissions.length} of {loanCommissionsList.length} total loan commission records
                                </p>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                {/* Status Filter Tabs */}
                                <div style={{
                                    display: 'flex',
                                    background: '#f1f5f9',
                                    padding: '3px',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    {["ALL", "PENDING", "PAID"].map((st) => (
                                        <button
                                            key={st}
                                            onClick={() => setFilterStatus(st)}
                                            style={{
                                                border: 'none',
                                                background: filterStatus === st ? '#ffffff' : 'transparent',
                                                color: filterStatus === st ? '#0f172a' : '#64748b',
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                boxShadow: filterStatus === st ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            {st}
                                        </button>
                                    ))}
                                </div>

                                {/* Live Search Input */}
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        placeholder="Search by amount, date..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{
                                            padding: '8px 12px 8px 34px',
                                            fontSize: '13px',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '8px',
                                            outline: 'none',
                                            width: '210px',
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                        }}
                                    />
                                    <div style={{ position: 'absolute',top:'6px', left: '10px', pointerEvents: 'none', color: '#94a3b8', width: '14px', height: '14px', display: 'flex', alignItems: 'center' }}>
                                        <Search style={{ width: "100%", margin: "auto" }} />
                                    </div>
                                    {searchTerm && (
                                        <button
                                            onClick={() => setSearchTerm("")}
                                            style={{
                                                position: 'absolute',
                                                right: '8px',
                                                background: 'none',
                                                border: 'none',
                                                color: '#94a3b8',
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                fontWeight: '700'
                                            }}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Table Content */}
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '13px',
                                textAlign: 'left',
                                minWidth: '1000px'
                            }}>
                                <thead>
                                    <tr style={{
                                        background: '#f8fafc',
                                        color: '#475569',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        borderBottom: '1px solid #e2e8f0'
                                    }}>
                                        <th style={{ padding: '14px 16px', width: '50px', textAlign: 'center', whiteSpace: 'nowrap' }}>#</th>
                                        <th style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>Disburse Date</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>Loan Amount (₹)</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'center', whiteSpace: 'nowrap' }}>Comm %</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>Commission (₹)</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>Prior FY Comm (₹)</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>Cumulative (₹)</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'center', whiteSpace: 'nowrap' }}>TDS %</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>TDS Amount (₹)</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>Net Payout (₹)</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'center', whiteSpace: 'nowrap' }}>Status</th>
                                        <th style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>Added On</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCommissions.length === 0 ? (
                                        <tr>
                                            <td colSpan={12} style={{ padding: '60px 20px', textAlign: 'center', color: '#94a3b8' }}>
                                                <div style={{ width: '48px', height: '48px', margin: '0 auto 12px auto', opacity: 0.4 }}>
                                                    <FileText style={{ width: "100%", margin: "auto" }} />
                                                </div>
                                                <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#334155', fontWeight: '700' }}>
                                                    No Records Found
                                                </h4>
                                                <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                                                    {searchTerm ? "No transactions match your search filter." : `No commission records available for Financial Year ${selectedFY}.`}
                                                </p>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredCommissions.map((row, index) => {
                                            const badge = getStatusBadge(row.payoutStatus);
                                            const isEven = index % 2 === 0;
                                            return (
                                                <tr
                                                    key={row.id || index}
                                                    style={{
                                                        backgroundColor: isEven ? '#ffffff' : '#f8fafc',
                                                        borderBottom: '1px solid #f1f5f9',
                                                        transition: 'background-color 0.15s ease'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isEven ? '#ffffff' : '#f8fafc'}
                                                >
                                                    {/* S.No */}
                                                    <td style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                                        {index + 1}
                                                    </td>

                                                    {/* Disburse Date */}
                                                    <td style={{ padding: '16px', fontWeight: '700', color: '#0f172a', whiteSpace: 'nowrap' }}>
                                                        {formatDate(row.disburseDate)}
                                                    </td>

                                                    {/* Loan Amount */}
                                                    <td style={{ padding: '16px', textAlign: 'right', fontWeight: '700', color: '#0f172a', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                        {formatCurrency(row.loanAmount)}
                                                    </td>

                                                    {/* Commission Percentage */}
                                                    <td style={{ padding: '16px', textAlign: 'center', color: '#475569', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                                        <span style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>
                                                            {row.commissionPercentage != null ? `${row.commissionPercentage}%` : "-"}
                                                        </span>
                                                    </td>

                                                    {/* Commission Amount */}
                                                    <td style={{ padding: '16px', textAlign: 'right', fontWeight: '700', color: '#7c3aed', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                        {formatCurrency(row.commissionAmount)}
                                                    </td>

                                                    {/* Prior FY Commission */}
                                                    <td style={{ padding: '16px', textAlign: 'right', color: '#64748b', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                        {formatCurrency(row.priorFyCommission)}
                                                    </td>

                                                    {/* Cumulative Commission */}
                                                    <td style={{ padding: '16px', textAlign: 'right', fontWeight: '600', color: '#334155', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                        {formatCurrency(row.cumulativeCommission)}
                                                    </td>

                                                    {/* TDS Percentage */}
                                                    <td style={{ padding: '16px', textAlign: 'center', color: '#475569', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                                        <span style={{ background: '#fff7ed', color: '#ea580c', padding: '3px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>
                                                            {row.tdsPercentage != null ? `${row.tdsPercentage}%` : "0%"}
                                                        </span>
                                                    </td>

                                                    {/* TDS Amount */}
                                                    <td style={{ padding: '16px', textAlign: 'right', fontWeight: '700', color: '#ea580c', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                        {formatCurrency(row.tdsAmount)}
                                                    </td>

                                                    {/* Net Payout Amount */}
                                                    <td style={{ padding: '16px', textAlign: 'right', fontWeight: '800', color: '#059669', fontSize: '13.5px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                        {formatCurrency(row.netPayoutAmount)}
                                                    </td>

                                                    {/* Status Badge */}
                                                    <td style={{ padding: '16px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                        <span style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            padding: '4px 10px',
                                                            fontSize: '11px',
                                                            fontWeight: '700',
                                                            borderRadius: '20px',
                                                            backgroundColor: badge.bg,
                                                            color: badge.color,
                                                            border: `1px solid ${badge.border}`,
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            <span style={{
                                                                width: '6px',
                                                                height: '6px',
                                                                borderRadius: '50%',
                                                                backgroundColor: badge.dotColor
                                                            }} />
                                                            {badge.label}
                                                        </span>
                                                    </td>

                                                    {/* Added On */}
                                                    <td style={{ padding: '16px', color: '#64748b', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                        {formatDate(row.addedOn)}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>

                                {/* Table Totals Summary Footer */}
                                {filteredCommissions.length > 0 && (
                                    <tfoot>
                                        <tr style={{
                                            backgroundColor: '#f8fafc',
                                            borderTop: '2px solid #cbd5e1',
                                            fontWeight: '800',
                                            color: '#0f172a'
                                        }}>
                                            <td colSpan={2} style={{ padding: '18px 16px', textAlign: 'center', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                                Summary ({filteredCommissions.length} Loans)
                                            </td>
                                            <td style={{ padding: '18px 16px', textAlign: 'right', fontSize: '14px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                {formatCurrency(filteredCommissions.reduce((acc, curr) => acc + (Number(curr.loanAmount) || 0), 0))}
                                            </td>
                                            <td style={{ padding: '18px 16px', textAlign: 'center', whiteSpace: 'nowrap' }}>-</td>
                                            <td style={{ padding: '18px 16px', textAlign: 'right', color: '#7c3aed', fontSize: '14px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                {formatCurrency(filteredCommissions.reduce((acc, curr) => acc + (Number(curr.commissionAmount) || 0), 0))}
                                            </td>
                                            <td colSpan={3} style={{ padding: '18px 16px', textAlign: 'center', whiteSpace: 'nowrap' }}>-</td>
                                            <td style={{ padding: '18px 16px', textAlign: 'right', color: '#ea580c', fontSize: '14px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                {formatCurrency(filteredCommissions.reduce((acc, curr) => acc + (Number(curr.tdsAmount) || 0), 0))}
                                            </td>
                                            <td style={{ padding: '18px 16px', textAlign: 'right', color: '#059669', fontSize: '15px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                {formatCurrency(filteredCommissions.reduce((acc, curr) => acc + (Number(curr.netPayoutAmount) || 0), 0))}
                                            </td>
                                            <td colSpan={2} style={{ padding: '18px 16px', whiteSpace: 'nowrap' }}></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                </>
            )}

            {!loading && !statementData && !error && (
                <div style={{
                    padding: '70px 20px',
                    textAlign: 'center',
                    background: '#ffffff',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.05)'
                }}>
                    <div style={{ width: '56px', height: '56px', margin: '0 auto 16px auto', color: '#94a3b8' }}>
                        <FileText style={{ width: "100%", margin: "auto" }} />
                    </div>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                        No Commission Data Available
                    </h3>
                    <p style={{ margin: 0, fontSize: '13.5px', color: '#64748b' }}>
                        Select a financial year or click refresh to load the payout statement.
                    </p>
                </div>
            )}
        </div>
    );
}
